<template>
  <main class="cover-auth">
    <div class="widget-auth">
      <!-- Profil utilisateur -->
      <div class="tag-state lg rounded-50 flex align-items-center gap-4 px-4 py-4 mb-6">
        <img 
          :src="userPhoto || '/public/assets/users/300w/user2.jpg'" 
          class="rounded-100 overflow-hidden size-2xl" 
          alt="Photo de profil"
        />
        <div class="px-3">
          <h5 class="my-1">{{ userName }}</h5>
          <span class="text-sm text-grey-600">{{ userEmail }} | {{ userCollectivite }}</span>
        </div>
        <div class="rounded-100 overflow-hidden size-2xl ml-auto">
          <img src="/public/icons/icon-coop-finance.svg" width="100%" alt="Coop Finance" />
        </div>
      </div>

      <h2 class="font-semibold text-center mt-6 mb-5">Créer votre code de sécurité</h2>

      <form class="form" @submit.prevent="handleSubmit">
        <PinInput 
          v-model="securityCode" 
          input-id="security-code"
          :length="4"
          @complete="handlePinComplete"
        />

        <button 
          type="submit" 
          class="btn lg w-100 flex-center mt-5"
          :disabled="!isCodeComplete || isSubmitting"
        >
          <img src="/public/icons/icon-valider.svg" class="btn-icon" alt="" />
          <span>{{ isSubmitting ? 'Validation...' : 'Valider' }}</span>
        </button>
      </form>

      <p class="text-read text-grey-600 text-center mt-5">
        Votre code de sécurité sera utilisé pour vous permettre d'accéder à votre compte.
      </p>
    </div>
  </main>
</template>

<script lang="ts">
import { defineComponent, computed, ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import PinInput from '@/components/PinInput.vue'
import authService from '@/services/authService'
import { showAlert } from '@/utils/swal'

export default defineComponent({
  name: 'SignUpActivation',
  components: {
    PinInput
  },
  setup() {
    const router = useRouter()
    const securityCode = ref('')
    const isSubmitting = ref(false)
    const userName = ref('')
    const userEmail = ref('')
    const userCollectivite = ref('')
    const userPhoto = ref('')

    // Vérifier que les données d'inscription sont présentes
    onMounted(() => {
      // Vérifier d'abord si c'est un flux d'invitation
      const invitationUserId = sessionStorage.getItem('invitation_user_id')

      if (invitationUserId) {
        // Flux d'invitation
        const nom = sessionStorage.getItem('invitation_user_nom') || ''
        const prenom = sessionStorage.getItem('invitation_user_prenom') || ''
        userName.value = `${prenom} ${nom}`
        userEmail.value = sessionStorage.getItem('invitation_user_email') || ''
        userCollectivite.value = sessionStorage.getItem('invitation_user_collectivite') || 'Coop Finance'
      } else {
        // Flux d'inscription classique
        const registerDataStr = sessionStorage.getItem('registerData')
        if (!registerDataStr) {
          // Pas de données d'inscription, rediriger vers signup
          router.push('/auth/sign-up')
          return
        }

        const registerData = JSON.parse(registerDataStr)
        userName.value = `${registerData.nom} ${registerData.prenom}`
        userEmail.value = registerData.email
        userCollectivite.value = registerData.collectivite || 'Non définie'
      }
    })

    const isCodeComplete = computed(() => {
      return securityCode.value.length === 4 && /^\d{4}$/.test(securityCode.value)
    })

    const handlePinComplete = (code: string) => {
      securityCode.value = code
    }

    const handleSubmit = async () => {
      if (!isCodeComplete.value) {
        await showAlert.warning('Veuillez saisir un code de sécurité à 4 chiffres')
        return
      }

      isSubmitting.value = true

      try {
        // Appel à l'API pour définir le code PIN
        const response = await authService.setPin(securityCode.value)

        if (response.status === 0) {
          // Succès
          // await showAlert.success('Code de sécurité créé avec succès !')

          // Vérifier si c'est un flux d'invitation
          const invitationUserId = sessionStorage.getItem('invitation_user_id')

          if (invitationUserId) {
            // Flux d'invitation : aller vers quotité cessible
            router.push('/auth/sign-up-quotite')
          } else {
            // Flux classique : nettoyer registerData et aller vers KYC
            sessionStorage.removeItem('registerData')
            router.push('/auth/sign-up-kyc')
          }
        } else {
          // Erreur
          await showAlert.error(response.message || 'Erreur lors de la création du code')
        }
      } catch (error) {
        console.error('Erreur lors de la création du code:', error)
        await showAlert.error('Une erreur est survenue. Veuillez réessayer.')
      } finally {
        isSubmitting.value = false
      }
    }

    return {
      securityCode,
      userName,
      userEmail,
      userCollectivite,
      userPhoto,
      isCodeComplete,
      isSubmitting,
      handlePinComplete,
      handleSubmit
    }
  }
})
</script>

<style scoped>
.form-control-code {
  width: 60px;
  height: 60px;
  text-align: center;
  font-size: 24px;
  font-weight: bold;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.form-control-code:focus {
  outline: none;
  border-color: var(--primary-color, #007bff);
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.form-control-code:not(:placeholder-shown) {
  border-color: var(--primary-color, #007bff);
  background-color: #f8f9fa;
}

.flex {
  display: flex;
  justify-content: center;
}

.gap-4 {
  gap: 1rem;
}

.tag-state {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
}

.rounded-50 {
  border-radius: 50px;
}

.rounded-100 {
  border-radius: 100%;
}

.size-2xl {
  width: 60px;
  height: 60px;
  object-fit: cover;
}

.text-grey-600 {
  color: #6c757d;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 480px) {
  .form-control-code {
    width: 50px;
    height: 50px;
    font-size: 20px;
  }
}
</style>

